import { useState, useEffect } from "react";
import { CreditCard, Calendar, FileText, DollarSign } from "lucide-react";
import Modal from "../ui/Modal";

// Payment dates are calendar dates stored at UTC midnight; local getters shift
// them a day back west of UTC. DD/MM/YYYY to match the invoices.
const formatPaymentDate = (date) => {
  if (!date) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

const PaymentModal = ({ invoice, payment = null, onSave, onClose, settings }) => {
  const [formData, setFormData] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Bank Transfer',
    reference: '',
    notes: ''
  });

  const isEditing = !!payment;

  useEffect(() => {
    if (payment) {
      setFormData({
        amount: payment.amount.toString(),
        paymentDate: payment.paymentDate,
        paymentMethod: payment.paymentMethod,
        reference: payment.reference || '',
        notes: payment.notes || ''
      });
    }
  }, [payment]);

  const formatCurrency = (amount) => `${settings.currency}${parseFloat(amount || 0).toFixed(2)}`;

  // Calculate invoice total
  const calculateInvoiceTotal = () => {
    const subtotal = invoice.items?.reduce((sum, item) => sum + (item.quantity * item.price), 0) || 0;
    const discount = subtotal * (invoice.discountPercentage || 0) / 100;
    const afterDiscount = subtotal - discount;
    const tax = settings.taxExempt ? 0 : afterDiscount * (settings.taxPercentage || 10) / 100;
    return afterDiscount + tax;
  };

  const invoiceTotal = calculateInvoiceTotal();
  const totalPaid = invoice.payments?.reduce((sum, p) => sum + p.amount, 0) || 0;
  
  // For editing, exclude the current payment from calculations
  const otherPaymentsTotal = isEditing 
    ? invoice.payments?.reduce((sum, p) => p._id !== payment._id ? sum + p.amount : sum, 0) || 0
    : totalPaid;
  
  const remainingAmount = invoiceTotal - otherPaymentsTotal;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const paymentAmount = parseFloat(formData.amount);
    
    if (paymentAmount <= 0) {
      alert('Payment amount must be greater than 0');
      return;
    }
    
    if (paymentAmount > remainingAmount) {
      if (!window.confirm(`Payment amount (${formatCurrency(paymentAmount)}) exceeds remaining balance (${formatCurrency(remainingAmount)}). Continue anyway?`)) {
        return;
      }
    }

    const paymentData = {
      amount: paymentAmount,
      paymentDate: formData.paymentDate,
      paymentMethod: formData.paymentMethod,
      reference: formData.reference,
      notes: formData.notes
    };

    if (isEditing) {
      paymentData._id = payment._id;
    } else {
      paymentData._id = Math.random().toString(36).substr(2, 9);
      paymentData.createdAt = new Date().toISOString();
    }

    onSave(paymentData, isEditing);
  };

  const setFullPayment = () => {
    setFormData({
      ...formData,
      amount: remainingAmount.toFixed(2)
    });
  };

  const paymentMethods = [
    'Bank Transfer',
    'Cheque',
    'Cash',
    'Credit Card',
    'Debit Card',
    'PayPal',
    'Stripe',
    'Direct Debit',
    'Standing Order',
    'Other'
  ];

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        <div className="flex items-center">
          <CreditCard className="w-5 h-5 mr-2" />
          {isEditing ? 'Edit Payment' : 'Record Payment'} - {invoice.number}
        </div>
      }
      size="large"
      footer={
        <div className="flex gap-3">
          <button 
            type="button" 
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            onClick={handleSubmit}
            disabled={!formData.amount || parseFloat(formData.amount) <= 0}
          >
            <DollarSign className="w-4 h-4 mr-1" />
            {isEditing ? 'Update Payment' : 'Record Payment'}
          </button>
        </div>
      }
    >
      <div className="p-0">
            {/* Invoice Summary */}
            <div className="bg-white border border-gray-200 rounded-lg mb-6">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h6 className="text-sm font-semibold text-gray-900 mb-0">Invoice Summary</h6>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Invoice Total</div>
                    <div className="font-bold text-gray-900">{formatCurrency(invoiceTotal)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Total Paid</div>
                    <div className="font-bold text-green-600">{formatCurrency(totalPaid)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Remaining</div>
                    <div className="font-bold text-yellow-600">{formatCurrency(remainingAmount)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Status</div>
                    <div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        remainingAmount <= 0 ? 'bg-green-100 text-green-800' :
                        totalPaid > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {remainingAmount <= 0 ? 'Fully Paid' :
                         totalPaid > 0 ? 'Partially Paid' : 'Unpaid'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Previous Payments */}
            {invoice.payments && invoice.payments.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg mb-6">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h6 className="text-sm font-semibold text-gray-900 mb-0">Previous Payments</h6>
                </div>
                <div className="p-4">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {invoice.payments.map(payment => (
                          <tr key={payment._id}>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {formatPaymentDate(payment.paymentDate)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {payment.paymentMethod}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                              {payment.reference || '-'}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                              {formatCurrency(payment.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Form */}
            <div className="bg-white border border-gray-200 rounded-lg">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h6 className="text-sm font-semibold text-gray-900 mb-0">
                  {isEditing ? 'Edit Payment' : 'New Payment'}
                </h6>
              </div>
              <div className="p-4">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount *</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 text-sm text-gray-900 bg-gray-200 border border-r-0 border-gray-300 rounded-l-md">
                        {settings.currency}
                      </span>
                      <input
                        type="number"
                        className="rounded-none bg-gray-50 border text-gray-900 focus:ring-blue-500 focus:border-blue-500 block flex-1 min-w-0 w-full text-sm border-gray-300 p-2.5"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        placeholder="0.00"
                        step="0.01"
                        min="0.01"
                        required
                      />
                      <button
                        type="button"
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={setFullPayment}
                        disabled={remainingAmount <= 0}
                      >
                        Full
                      </button>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Remaining: {formatCurrency(remainingAmount)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      Payment Date *
                    </label>
                    <input
                      type="date"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      value={formData.paymentDate}
                      onChange={(e) => setFormData({...formData, paymentDate: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <CreditCard className="w-4 h-4 mr-1" />
                      Payment Method *
                    </label>
                    <select
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData({...formData, paymentMethod: e.target.value})}
                      required
                    >
                      {paymentMethods.map(method => (
                        <option key={method} value={method}>{method}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reference/Transaction ID</label>
                    <input
                      type="text"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      value={formData.reference}
                      onChange={(e) => setFormData({...formData, reference: e.target.value})}
                      placeholder="e.g., Cheque number, transaction ID"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FileText className="w-4 h-4 mr-1" />
                    Notes
                  </label>
                  <textarea
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes about this payment..."
                  />
                </div>

                {/* Payment Preview */}
                {formData.amount && (
                  <div className="bg-blue-100 border border-blue-400 text-blue-800 px-4 py-3 rounded">
                    <div className="font-semibold">Payment Preview:</div>
                    <div className="mt-1">
                      Amount: {formatCurrency(parseFloat(formData.amount) || 0)}
                      <br />
                      New Balance: {formatCurrency(remainingAmount - (parseFloat(formData.amount) || 0))}
                      <br />
                      Status after payment: 
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ml-2 ${
                        (remainingAmount - (parseFloat(formData.amount) || 0)) <= 0 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {(remainingAmount - (parseFloat(formData.amount) || 0)) <= 0 ? 'Fully Paid' : 'Partially Paid'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
      </div>
    </Modal>
  );
};

export default PaymentModal;